/**
 * Cancellation policy as shown in the UI.
 *
 * The live numbers come from GET /config/policy (see contexts/PolicyContext)
 * so what people see always matches what the server enforces in that
 * environment. The constants here are only the fallback while that request
 * is in flight or if it fails, and they match the backend defaults.
 */
export interface Policy {
  /** Cancel at least this many hours before the start and the room is released at no charge. */
  windowHours: number;
  /** A booking cancelled within this many minutes of being made is released at no charge (0 = off). */
  graceMinutes: number;
  /** Rollout switch: can clinicians cancel their own room/pod bookings? */
  selfCancelEnabled: boolean;
  /** Rollout switch: is the "?" guided-help button shown? */
  guidesEnabled: boolean;
}

export const CANCELLATION_WINDOW_HOURS = 24;
export const BOOKING_GRACE_MINUTES = 15;

export const DEFAULT_POLICY: Policy = {
  windowHours: CANCELLATION_WINDOW_HOURS,
  graceMinutes: BOOKING_GRACE_MINUTES,
  selfCancelEnabled: true,
  guidesEnabled: true,
};

const HOUR_MS = 60 * 60 * 1000;

export const hoursUntil = (startIso: string, now: Date = new Date()): number =>
  (new Date(startIso).getTime() - now.getTime()) / HOUR_MS;

/** True while the booking is still inside its mistake window. */
export const isWithinGrace = (
  createdIso: string | undefined | null, now: Date = new Date(), graceMinutes: number = BOOKING_GRACE_MINUTES,
): boolean =>
  graceMinutes > 0 && !!createdIso && now.getTime() - new Date(createdIso).getTime() <= graceMinutes * 60 * 1000;

/** When the mistake window closes, e.g. "2:14 PM". */
export const formatGraceDeadline = (createdIso: string, graceMinutes: number = BOOKING_GRACE_MINUTES): string =>
  new Date(new Date(createdIso).getTime() + graceMinutes * 60 * 1000)
    .toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });

/** True when cancelling now would keep the room held and billed. */
export const isInsideCancellationWindow = (
  startIso: string, now: Date = new Date(), createdIso?: string | null,
  windowHours: number = CANCELLATION_WINDOW_HOURS, graceMinutes: number = BOOKING_GRACE_MINUTES,
): boolean =>
  hoursUntil(startIso, now) < windowHours && !isWithinGrace(createdIso, now, graceMinutes);

/** The last moment a cancellation still releases the room. */
export const freeCancellationDeadline = (startIso: string, windowHours: number = CANCELLATION_WINDOW_HOURS): Date =>
  new Date(new Date(startIso).getTime() - windowHours * HOUR_MS);

/** e.g. "Tue, Sep 22, 1:00 PM" in the viewer's locale. */
export const formatDeadline = (startIso: string, windowHours: number = CANCELLATION_WINDOW_HOURS): string =>
  freeCancellationDeadline(startIso, windowHours).toLocaleString(undefined, {
    weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });

/** "twenty-four (24)" style for legal text. */
export const spelledNumber = (n: number): string => {
  const words: Record<number, string> = {
    0: 'zero', 1: 'one', 2: 'two', 3: 'three', 5: 'five', 10: 'ten', 12: 'twelve', 15: 'fifteen', 24: 'twenty-four',
    30: 'thirty', 36: 'thirty-six', 48: 'forty-eight', 60: 'sixty', 72: 'seventy-two', 96: 'ninety-six',
  };
  return words[n] ? `${words[n]} (${n})` : String(n);
};
